/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import {
  FoodmaxapipbCreateModifierGroupResponse,
  FoodmaxapipbUpdateModifierAvailableStatusV2Response,
} from "generated/foodmaxapi.type"
import { isDev } from "constants/domain"
import { createRequestHeaders } from "./apiClient"
import {
  HttpMethod,
  ChangeModifierAvailabilityRequest,
  CreateModifierGroupRequest,
  DeleteModifierGroupRequest,
  LinkItemToModifierGroupRequest,
  ProofModifierGroupRequest,
} from "./types"
import { endpoints } from "./constants"
import { foodMerchantBaseApi } from "./baseApis"

const mexType = isDev()
  ? { "mex-type": "MEXUSERS" }
  : { "x-grabkit-grab-serviceid": "MEXUSERS" }

export const modifierGroupApi = foodMerchantBaseApi.injectEndpoints({
  endpoints: builder => ({
    changeModifierAvailability: builder.mutation<
      FoodmaxapipbUpdateModifierAvailableStatusV2Response,
      ChangeModifierAvailabilityRequest
    >({
      query: ({ merchantID, availableStatus, modifierIDs }) => ({
        url: endpoints.changeModifierAvailability,
        method: HttpMethod.PUT,
        headers: { ...createRequestHeaders({ merchantID }), ...mexType },
        data: {
          availableStatus,
          modifierIDs,
        },
      }),
    }),
    linkItemToModifierGroup: builder.mutation<
      unknown,
      LinkItemToModifierGroupRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        modifierGroupID,
        itemID,
        menuGroupID,
        showError = true,
      }) => ({
        url: menuGroupID
          ? endpoints.linkItemToModifierGroupMenuGroup
          : endpoints.linkItemToModifierGroup,
        method: "POST",
        headers: {
          ...createRequestHeaders({ merchantID, merchantGroupID }),
          ...mexType,
        },
        data: {
          itemID,
          modifierGroupID,
          menuGroupID,
        },
        extraOptions: { showError },
      }),
    }),
    createModifierGroup: builder.mutation<
      FoodmaxapipbCreateModifierGroupResponse,
      CreateModifierGroupRequest
    >({
      query: ({ merchantID, merchantGroupID, menuGroupID, modifierGroup }) => ({
        url: menuGroupID
          ? endpoints.createMenuGroupModifierGroup
          : endpoints.createModifierGroup,
        method: HttpMethod.POST,
        headers: {
          ...createRequestHeaders({ merchantID, merchantGroupID }),
          ...mexType,
        },
        params: menuGroupID ? { menuGroupID } : undefined,
        data: { modifierGroup },
        extraOptions: { showError: false },
      }),
    }),
    deleteModifierGroup: builder.mutation<object, DeleteModifierGroupRequest>({
      query: ({
        merchantID,
        merchantGroupID,
        modifierGroupID,
        menuGroupID,
      }) => ({
        url: menuGroupID
          ? `${endpoints.deleteModifierGroupMenuGroup}/${modifierGroupID}`
          : `${endpoints.deleteModifierGroupV2}/${modifierGroupID}`,
        method: HttpMethod.DELETE,
        headers: {
          ...createRequestHeaders({ merchantID, merchantGroupID }),
          ...mexType,
        },
        params: menuGroupID ? { menuGroupID } : undefined,
      }),
    }),
    proofModifierGroup: builder.mutation<unknown, ProofModifierGroupRequest>({
      query: ({
        merchantID,
        merchantGroupID,
        modifierGroupID,
        menuGroupID,
        showError = false,
      }) => ({
        url: menuGroupID
          ? `${endpoints.proofModifierGroupMenuGroup}/${modifierGroupID}/proof`
          : `${endpoints.proofModifierGroup}/${modifierGroupID}/proof`,
        method: HttpMethod.POST,
        headers: {
          ...createRequestHeaders({ merchantID, merchantGroupID }),
          ...mexType,
        },
        params: menuGroupID ? { menuGroupID } : undefined,
        data: { proofImageURL: "" },
        extraOptions: { showError },
      }),
    }),
  }),
})

export const {
  useChangeModifierAvailabilityMutation,
  useLinkItemToModifierGroupMutation,
  useCreateModifierGroupMutation,
  useDeleteModifierGroupMutation,
  useProofModifierGroupMutation,
} = modifierGroupApi
